// src/fixtures/acmeOrgProfile.ts
// ============================================================
// CANONICAL ACME ORGANIZATION PROFILE — SINGLE SOURCE OF TRUTH
//
// One authored profile. The Organization Profile page derives EVERY field value, its
// summary card, and its completion/strength state from this module — there are no
// page-local values any more. /home's Profile card reads 100 from the shared
// personalization vector, and this profile computes to 100 from the seeded data, so the
// two agree BY CONSTRUCTION rather than by two hardcoded numbers that can drift.
//
// Lives at src/fixtures (not src/prototype/**) on purpose: ClientProfilePage is a SHARED
// page mounted both inside the EA flow and on the admin/QA routes, so importing a
// prototype-scoped module from it would be a cross-boundary import.
//
// COHERENCE — every value is authored to agree with what the demo already asserts:
//   · 1,850 beds matches the figure already narrated on screen
//   · VBC-forward posture (240k lives under risk, MSSP Enhanced, MA risk, bundles)
//     is what justifies Care Coordination ranking #1
//   · Pathfinder-Developing archetype (the p4 delta row) reads as a mid-maturity IDN
//   · 2.1% operating margin is the thin margin behind the
//     "Financial Sustainability & Margin Improvement" active priority
//
// FICTIONAL-SAFETY (deliberate — this ships to a public demo):
//   · email uses the reserved .example TLD (RFC 2606) — undeliverable by design
//   · phone is in the 555-01xx fictional range (NANP reserved for drama)
//   · both NPIs are Luhn-INVALID against the 80840 prefix, so they cannot collide with
//     any real provider NPI. Verify with the standard NPI check-digit algorithm before
//     ever changing them.
// ============================================================

export interface AcmeAddress {
  street: string;
  city: string;
  state: string;
  zip: string;
}

export interface AcmePrimaryContact {
  name: string;
  title: string;
  email: string;
  phone: string;
}

export interface AcmeOrganizationalNpi {
  npi: string;
  name: string;
}

/**
 * Keys mirror the `clients` table column names the profile form and the strength
 * calculator read. Where the calculator accepts an alternate spelling (`rural_pct` vs
 * `rural_percentage`) BOTH are provided, so neither reader has to know which one won.
 */
export const ACME_ORG_PROFILE = {
  // ---- Basic -------------------------------------------------------------
  organization_name: 'ACME Health System',
  // Must be one of ORGANIZATION_TYPES in ClientProfilePage; "IDN" is the descriptor,
  // not a selectable type. Picking a value outside that list renders an EMPTY select.
  organization_type: 'Health System',
  organization_subtype: 'Integrated Delivery Network (IDN)',
  ehr_vendor: 'Epic',

  // ---- Footprint ---------------------------------------------------------
  beds: 1850,
  total_beds: 1850,
  employed_providers: 2400,
  affiliates: 12,
  attributed_lives: 385000,
  lives_under_vbc_agreements: 240000,
  lives_under_vbc: 240000,

  // ---- Economics ---------------------------------------------------------
  operating_margin_pct: 2.1,
  annual_revenue: 3_200_000_000, // $3.2B
  // Payer mix sums to 100 with the self-pay/other balance (quality check wants 95–105).
  payer_mix_commercial_pct: 32,
  payer_mix_medicare_pct: 38,
  payer_mix_medicaid_pct: 22,
  self_pay_percentage: 8,

  // ---- Service lines -----------------------------------------------------
  service_lines: [
    'Cardiology',
    'Oncology',
    'Orthopedics',
    "Women's Health",
    'Behavioral Health',
    'Primary Care Network',
  ],

  // ---- VBC models --------------------------------------------------------
  vbc_models: [
    'MSSP ACO (Enhanced track)',
    'Medicare Advantage risk contracts',
    'Commercial shared savings',
    'Joint-replacement bundles',
  ],

  // ---- Geography ---------------------------------------------------------
  service_area_states: ['NC', 'SC', 'VA'],
  // Sums to 100 (quality check wants 95–105). Both key spellings supplied.
  rural_percentage: 28,
  urban_percentage: 47,
  suburban_percentage: 25,
  rural_pct: 28,
  urban_pct: 47,
  suburban_pct: 25,
  market_competition_level: 'High',

  // ---- Address (collapsed section) --------------------------------------
  address: {
    street: '400 Meridian Parkway',
    city: 'Charlotte',
    state: 'NC',
    zip: '28202',
  } as AcmeAddress,

  // ---- Primary contact (collapsed section) ------------------------------
  primary_contact: {
    name: 'Dana Whitfield',
    title: 'VP of Analytics',
    email: 'dana.whitfield@acmehealth.example', // reserved TLD — undeliverable
    phone: '(704) 555-0142', // 555-01xx fictional range
  } as AcmePrimaryContact,

  // ---- Organizational NPIs (collapsed section) --------------------------
  // DELIBERATELY CHECKSUM-INVALID. All three fail the standard NPI Luhn check against the
  // 80840 prefix, which is exactly why they are safe to ship in a public demo: they cannot
  // collide with any real provider's NPI. DO NOT "correct" these into valid numbers — a valid
  // 10-digit NPI belongs to a real organisation somewhere.
  //
  // `name` is NOT authored here: it is resolved at render time by the demo registry lookup
  // (src/services/npiRegistryDemo.ts) and shown read-only, so the number is the single input.
  organizational_npis: [
    { npi: '1000000010', name: '' },
    { npi: '1000000028', name: '' },
    { npi: '1000000036', name: '' },
  ] as AcmeOrganizationalNpi[],
} as const;

export type AcmeOrgProfile = typeof ACME_ORG_PROFILE;

/** Nested objects that must be merged key-by-key rather than replaced wholesale. */
const NESTED_KEYS = ['address', 'primary_contact'] as const;

/**
 * Is an incoming value real enough to override a seeded one?
 *
 * null / undefined / '' / [] / {} all mean "the backend has no value here", NOT "the value
 * is empty". This distinction is the whole fix — see mergeProfileResponse.
 */
function isPresent(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v as object).length > 0;
  return true;
}

/**
 * Merge a backend profile response over the current (seeded) profile WITHOUT letting absent
 * backend values erase seeded ones.
 *
 * THE BUG THIS FIXES: fetchProfile did `setProfile(p => ({ ...p, ...data.data }))`. A plain
 * spread cannot tell "the backend returned null for market_competition_level" from "the
 * backend has no opinion" — both overwrite. With a local backend running on :8080 the demo
 * client comes back sparse, so exactly the sparse columns (Lives Under VBC, Market Competition
 * Level, and the whole address / primary_contact objects) blanked back to placeholders while
 * the columns the backend does populate kept showing the seed. That is why the page looked
 * half-bound and why it behaved differently with and without a backend running.
 *
 * Fixing it here rather than per-field means every future seeded field is protected by
 * construction, and the page renders identically online and offline.
 */
export function mergeProfileResponse(
  current: Record<string, unknown>,
  incoming: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  if (!incoming) return current;
  const out: Record<string, unknown> = { ...current };

  for (const [key, value] of Object.entries(incoming)) {
    if (!isPresent(value)) continue; // backend has nothing here — keep the seeded value

    if ((NESTED_KEYS as readonly string[]).includes(key)) {
      // Merge sub-field by sub-field, so a backend address carrying only { state: 'NC' }
      // does not wipe the seeded street/city/zip.
      const base = (current[key] ?? {}) as Record<string, unknown>;
      const next = value as Record<string, unknown>;
      const merged: Record<string, unknown> = { ...base };
      for (const [k, v] of Object.entries(next)) if (isPresent(v)) merged[k] = v;
      out[key] = merged;
      continue;
    }

    out[key] = value;
  }

  return out;
}

/**
 * A mutable copy for React state. The constant above is deeply `as const`, so handing it
 * straight to setState would let an edit mutate the canonical seed.
 */
export function getAcmeOrgProfile(): Record<string, unknown> {
  return {
    ...ACME_ORG_PROFILE,
    service_lines: [...ACME_ORG_PROFILE.service_lines],
    vbc_models: [...ACME_ORG_PROFILE.vbc_models],
    service_area_states: [...ACME_ORG_PROFILE.service_area_states],
    address: { ...ACME_ORG_PROFILE.address },
    primary_contact: { ...ACME_ORG_PROFILE.primary_contact },
    organizational_npis: ACME_ORG_PROFILE.organizational_npis.map((n) => ({ ...n })),
  };
}

export default ACME_ORG_PROFILE;
